import { DOCUMENT_TYPE_APPLIES_TO, type DocumentTypeInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect } from 'react';
import type { DocumentType } from './reference-data.api';

interface DocumentTypeFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: DocumentType;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: DocumentTypeInput) => Promise<boolean>;
  onClose: () => void;
}

const APPLIES_TO_OPTIONS = DOCUMENT_TYPE_APPLIES_TO.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));

/** One drawer for both create and edit — "edit every field" means there's no separate patch shape. */
export function DocumentTypeFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: DocumentTypeFormDrawerProps) {
  const [form] = Form.useForm<DocumentTypeInput>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(
        editing ?? {
          code: '',
          name: '',
          appliesTo: 'truck',
          legalHardBlock: false,
          crossBorderOnly: false,
          typicalValidityDays: undefined as unknown as number,
          reminderLeadDays: undefined as unknown as number,
        },
      );
    }
  }, [open, editing, form]);

  async function submit(values: DocumentTypeInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit document type' : 'New document type'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<DocumentTypeInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="appliesTo" label="Applies to" rules={[{ required: true, message: 'Choose what this applies to' }]}>
          <Select options={APPLIES_TO_OPTIONS} />
        </Form.Item>
        <Form.Item name="typicalValidityDays" label="Typical validity (days)" rules={[{ required: true, message: 'Enter a validity in days' }]}>
          <InputNumber className="w-full" min={1} step={1} />
        </Form.Item>
        <Form.Item name="reminderLeadDays" label="Reminder lead time (days)" rules={[{ required: true, message: 'Enter a reminder lead time in days' }]}>
          <InputNumber className="w-full" min={1} step={1} />
        </Form.Item>
        <Form.Item name="legalHardBlock" label="Legal hard block" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="crossBorderOnly" label="Cross-border only" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create document type'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
